<template>
  <div class="franchise-data-hub" :class="{ embedded: props.embedded }">
    <div class="hub-container">
      <CoiPageHeader
        v-if="!props.embedded"
        title="Franchise Data"
        subtitle="FranConnect sync monitoring, active locations, sync issues, and match hygiene"
        icon="fas fa-store"
      >
        <template #actions>
          <router-link to="/coi/integrations" class="btn-secondary">
            <i class="fas fa-plug"></i> Integrations
          </router-link>
          <router-link to="/clients" class="btn-secondary">
            <i class="fas fa-home"></i> Home
          </router-link>
          <router-link :to="complianceHealthLink" class="btn-secondary">
            <i class="fas fa-heartbeat"></i> Compliance Health
          </router-link>
        </template>
      </CoiPageHeader>

      <div v-if="accessDenied" class="access-denied">
        <i class="fas fa-lock"></i>
        <h2>Franchise Data not available</h2>
        <p>{{ denyMessage }}</p>
        <p v-if="allowedProjects.length" class="deny-hint">
          Your FranConnect projects:
          <router-link
            v-for="p in allowedProjects"
            :key="p.project_id"
            :to="`/projects/${p.project_id}/franchise-data`"
            class="deny-project-link"
          >
            {{ p.project_name }} ({{ p.project_id }})
          </router-link>
        </p>
        <router-link to="/clients">Return to Home</router-link>
      </div>

      <template v-else>
      <div class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab-btn"
          :class="{ active: activeTab === tab.id }"
          @click="setTab(tab.id)"
        >
          {{ tab.label }}
          <span v-if="tab.id === 'reconciliation' && gapFcCount > 0" class="tab-badge tab-badge-warn">
            {{ gapFcCount }}
          </span>
          <span v-if="tab.id === 'issues' && openIssuesCount > 0" class="tab-badge">
            {{ openIssuesCount }}
          </span>
          <span v-if="tab.id === 'match-hygiene' && matchHygieneOpenCount > 0" class="tab-badge">
            {{ matchHygieneOpenCount }}
          </span>
        </button>
      </div>

      <div class="tab-content">
        <FranchiseSyncOverview
          v-if="activeTab === 'overview'"
          ref="overviewRef"
          :project-id="projectId"
          @issues-count="openIssuesCount = $event"
          @gap-count="gapFcCount = $event"
          @open-reconciliation="setTab('reconciliation')"
        />

        <FranchiseSyncComparePanel
          v-if="activeTab === 'reconciliation'"
          ref="compareRef"
          :project-id="projectId"
          @updated="refreshOverview"
        />

        <FranchiseLocationsPanel
          v-if="activeTab === 'locations'"
          :project-id="projectId"
          embedded
          :show-stats="false"
          entity-links
        />

        <FranchiseSyncIssuesPanel
          v-if="activeTab === 'issues'"
          ref="issuesRef"
          :project-id="projectId"
          @updated="refreshOverview"
        />
      </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CoiPageHeader from '@/components/coi/CoiPageHeader.vue'
import FranchiseSyncOverview from '@/components/franchise/FranchiseSyncOverview.vue'
import FranchiseSyncComparePanel from '@/components/franchise/FranchiseSyncComparePanel.vue'
import FranchiseLocationsPanel from '@/components/franchise/FranchiseLocationsPanel.vue'
import FranchiseSyncIssuesPanel from '@/components/franchise/FranchiseSyncIssuesPanel.vue'
import franchiseHubContext from '@/services/franchiseHubContext'
import api from '@/services/api'

const props = defineProps({
  embedded: { type: Boolean, default: false },
  projectIdOverride: { type: Number, default: null },
  initialTab: { type: String, default: null },
})

const route = useRoute()
const router = useRouter()

const activeTab = ref('overview')
const openIssuesCount = ref(0)
const matchHygieneOpenCount = ref(0)
const gapFcCount = ref(0)
const overviewRef = ref(null)
const compareRef = ref(null)
const issuesRef = ref(null)
const accessDenied = ref(false)
const denyMessage = ref('')
const allowedProjects = ref([])
const hubProjectId = ref(null)

const tabs = [
  { id: 'overview', label: 'Sync & Status' },
  { id: 'reconciliation', label: 'FC Gap' },
  { id: 'locations', label: 'Active Locations' },
  { id: 'issues', label: 'Sync Issues' },
  { id: 'match-hygiene', label: 'Match Hygiene' },
]

const projectId = computed(() => {
  if (props.projectIdOverride) return props.projectIdOverride
  const raw = route.params.projectId
  const parsed = parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : hubProjectId.value
})

const complianceHealthLink = computed(() => ({
  path: '/compliance-health',
  query: { project_id: projectId.value },
}))

function setTab(tabId) {
  if (tabId === 'match-hygiene' && projectId.value) {
    router.push(`/projects/${projectId.value}/match-hygiene`)
    return
  }
  activeTab.value = tabId
  router.replace({
    path: route.path,
    query: { ...route.query, tab: tabId },
  })
}

function refreshOverview() {
  overviewRef.value?.refresh?.()
}

async function loadMatchHygieneOpenCount() {
  if (!projectId.value) return
  try {
    const response = await api.get('/match-hygiene/issues/', {
      params: {
        project_id: projectId.value,
        status: 'open',
        limit: 1,
        offset: 0,
      },
    })
    matchHygieneOpenCount.value = response.data?.data?.open_count || 0
  } catch {
    matchHygieneOpenCount.value = 0
  }
}

function redirectMatchHygieneTabIfNeeded() {
  const tab = props.initialTab || route.query.tab
  if (tab === 'match-hygiene' && projectId.value) {
    router.replace(`/projects/${projectId.value}/match-hygiene`)
    return true
  }
  return false
}

watch(
  () => route.query.tab,
  (tab) => {
    if (tab && tabs.some((t) => t.id === tab)) {
      activeTab.value = tab
    }
  },
  { immediate: true },
)

async function validateTenancy() {
  const ctx = await franchiseHubContext.load(true)
  hubProjectId.value = ctx.project_id
  allowedProjects.value = ctx.projects || []

  const routeProjectId = props.projectIdOverride || parseInt(route.params.projectId, 10)

  if (!ctx.visible || !ctx.hub_enabled) {
    accessDenied.value = true
    denyMessage.value = (
      'Your organization does not have FranConnect sync configured. '
      + 'Franchise Data is only available for organizations with an active FranConnect integration.'
    )
    return
  }

  if (!franchiseHubContext.canAccessProject(routeProjectId)) {
    accessDenied.value = true
    const allowed = allowedProjects.value.map((p) => p.project_id).join(', ') || 'none'
    denyMessage.value = (
      `Project ${routeProjectId} is outside your FranConnect scope. `
      + `You can only open Franchise Data for project(s): ${allowed}. `
      + 'JMFS franchise data (project 13) requires a JMFS org login, Platform Admin, or COMPLIANCE_OPS access.'
    )
    return
  }

  accessDenied.value = false
  denyMessage.value = ''
}

onMounted(async () => {
  await validateTenancy()
  if (accessDenied.value) return
  if (redirectMatchHygieneTabIfNeeded()) return
  const tabFromRoute = route.query.tab
  const tabFromProp = props.initialTab
  const tab = tabFromProp || tabFromRoute
  if (tab && tabs.some((t) => t.id === tab)) {
    activeTab.value = tab
  }
  await loadMatchHygieneOpenCount()
})

watch(() => route.params.projectId, validateTenancy)
</script>

<style scoped>
.franchise-data-hub {
  min-height: 100vh;
  background: #f1f5f9;
  padding: 1.5rem 1rem 3rem;
}

.franchise-data-hub.embedded {
  min-height: auto;
  padding: 0.5rem;
  background: transparent;
}

.hub-container {
  max-width: 1400px;
  margin: 0 auto;
}

.tab-bar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
}

.tab-btn {
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-btn.active {
  color: #1e40af;
  border-bottom-color: #1e40af;
}

.tab-badge {
  background: #dc2626;
  color: white;
  font-size: 0.7rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
}

.tab-badge-warn {
  background: #d97706;
}

.tab-content {
  background: transparent;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.875rem;
}

.access-denied {
  text-align: center;
  padding: 4rem 1rem;
  color: #64748b;
}

.access-denied a {
  color: #1e40af;
  font-weight: 600;
}

.deny-hint {
  margin: 1rem 0;
  font-size: 0.9rem;
}

.deny-project-link {
  display: inline-block;
  margin: 0.25rem 0.5rem;
}
</style>
